/* eslint-disable rxjs-angular/prefer-takeuntil */
import { ViewportScroller } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { PlatformService } from '@core/platform';
import { distinctUntilChanged, race, Subscription, timer } from 'rxjs';
import { audit, debounce, filter, tap } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { JssStateActionTypes } from '@core/jss-routing';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'wl-scroll-manager',
  template: '',
  standalone: true,
})
export class ScrollManagerComponent implements OnInit {
  subscription: Subscription | undefined;

  readonly #viewportScroller = inject(ViewportScroller);
  readonly #platformService = inject(PlatformService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #destroyRef = inject(DestroyRef);
  readonly #actions$ = inject(Actions);
  readonly #router = inject(Router);

  readonly #navigationChanges = this.#router.events.pipe(
    filter((event) => event instanceof NavigationEnd)
  );

  public readonly rootPlaceholderLoaded$ = this.#actions$.pipe(
    ofType(JssStateActionTypes.ROOT_PLACEHOLDER_LOADED)
  );

  public readonly dynamicPlaceholdersLoaded$ = this.#actions$.pipe(
    ofType(JssStateActionTypes.DYNAMIC_PLACEHOLDER_LOADED)
  );

  #handleScrollOnNavigation() {
    /**
     * NOTE: This effect handles navigation to anchors for both initial-page-load & after internal navigation
     */
    this.subscription = this.#navigationChanges
      .pipe(
        audit(() =>
          race(this.rootPlaceholderLoaded$, this.dynamicPlaceholdersLoaded$)
        ),
        /**
         * There is a 250ms delay before scrolling for 3 reasons:
         *   1. When the (loaded) event is emitted from <sc-placeholder>, it is only
         *      markedForCheck in the ChangeDetector, it still has to run.
         *   2. There could also be items in the placeholder that need to lazy-load,
         *      there is currently no way to wait for that.
         *   3. There could be multiple events of placeholders to load, so we only want to start scrolling after
         *      250ms of no new "loaded" events. After that we can assume every placeholder is loaded.
         */
        debounce(() => timer(250)),
        // If a scroll was already attempted on this navigation, do not try it again (to prevent a bad UX).
        distinctUntilChanged((previous, current) => previous.id === current.id),
        tap(() => {
          const { fragment } = this.#activatedRoute.snapshot;
          if (fragment) {
            // When the activatedRoute has a fragment, we scroll to it manually
            this.#viewportScroller.scrollToAnchor(fragment);
          }
          // When there is no fragment we let the RouterModule just handle scrolling to the top of the page.
          // This always happens because scrollPositionRestoration is disabled.
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  ngOnInit() {
    if (this.#platformService.isClient()) {
      this.#handleScrollOnNavigation();
    }
  }
}
